import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, fmtErr } from "../../../lib/api";
import { StatusBadge } from "../Dashboard";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../../components/ui/dialog";
import { Textarea } from "../../../components/ui/textarea";
import { Send, AtSign, ArrowRightLeft } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { toast } from "sonner";
import { VENDOR_STATUS, PROVIDER_FORBIDDEN_STATUSES, NO_CANCEL_STATUSES } from "./constants";
import { PriorityPill, PhotoBucket, DocumentsBucket, VatBreakdown, ShowInvoiceButton } from "./components";
import { StarsDisplay } from "../../../components/SupplierReview";
import { useAuth } from "../../../context/AuthContext";

function MiniLabel({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{children}</div>;
}

function fmtTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

/* Status-level task history (creation, status changes, accept/cancel). */
function TaskHistory({ taskId, refreshKey }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(`/maintenance/${taskId}/history`)
      .then(({ data }) => { if (alive) setRows(data); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [taskId, refreshKey]);
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-1 border-t border-zinc-200 pt-3" data-testid="sup-task-history">
      <MiniLabel>History</MiniLabel>
      <ul className="mt-1 space-y-1.5">
        {rows.map((r, i) => (
          <li key={i} className="text-xs text-zinc-600 flex items-baseline gap-2" data-testid={`sup-task-history-row-${i}`}>
            <span className="text-zinc-400 whitespace-nowrap">{(r.created_at || "").slice(0, 10)}</span>
            <span className="min-w-0">{r.summary}{r.by_user_name ? <span className="text-zinc-400"> · {r.by_user_name}</span> : null}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* Supplier ↔ yacht message thread on the supplier's own quote. */
function QuoteThread({ quote, onChanged }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [colleagues, setColleagues] = useState([]);
  const [mentions, setMentions] = useState([]);
  const { user } = useAuth();
  const threadRef = useRef(null);
  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [(quote.messages || []).length]);
  useEffect(() => {
    if (user?.is_supplier) {
      api.get("/supplier/team/colleagues")
        .then(({ data }) => setColleagues(data || []))
        .catch(() => {});
    }
  }, [user?.is_supplier]);
  const addMention = (c) => {
    setMsg((m) => `${m}${m && !m.endsWith(" ") ? " " : ""}@${c.name} `);
    setMentions((x) => [...new Set([...x, c.user_id])]);
  };
  const send = async () => {
    if (!msg.trim() || busy) return;
    setBusy(true);
    try {
      // Only notify teammates whose @Name is still present in the text.
      const active = mentions.filter((id) => {
        const c = colleagues.find((x) => x.user_id === id);
        return c && msg.includes(`@${c.name}`);
      });
      await api.post(`/quotes/${quote.id}/messages`, { text: msg.trim(), mentions: active });
      setMsg("");
      setMentions([]);
      await onChanged?.();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const canRemove = (m) => m.sender_kind === "supplier" && !m.deleted
    && Date.now() - new Date(m.at).getTime() < 15 * 60 * 1000;
  const removeMsg = async (mid) => {
    try { await api.delete(`/quotes/${quote.id}/messages/${mid}`); await onChanged?.(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <div data-testid="sup-task-messages">
      <div ref={threadRef} className="space-y-2.5 max-h-64 overflow-y-auto pr-1 mt-2">
        {(quote.messages || []).length === 0 && (
          <p className="text-xs text-zinc-400 text-center py-3">No messages yet — start the conversation with the yacht.</p>
        )}
        {(quote.messages || []).map((m) => {
          const mine = m.sender_kind === "supplier";
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${mine ? "bg-sky-600 text-white rounded-br-sm" : "bg-zinc-100 text-zinc-800 rounded-bl-sm"}`}>
                <div className={`text-[10px] mb-0.5 font-medium ${mine ? "text-sky-100" : "text-zinc-500"}`}>{m.sender_name}</div>
                {m.deleted
                  ? <div className="italic opacity-60">Message removed</div>
                  : <div className="whitespace-pre-wrap break-words">{m.text}</div>}
              </div>
              <div className="text-[10px] text-zinc-400 mt-0.5 px-1 flex items-center gap-2">
                {fmtTime(m.at)}
                {canRemove(m) && (
                  <button type="button" onClick={() => removeMsg(m.id)} className="text-zinc-400 hover:text-red-600 underline underline-offset-2" title="Remove this message (within 15 minutes of sending)" data-testid={`sup-task-msg-remove-${m.id}`}>Remove</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2.5">
        <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a message…" onKeyDown={(e) => e.key === "Enter" && send()} data-testid="sup-task-msg-input" />
        {colleagues.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="outline" className="shrink-0" title="Mention a teammate" data-testid="sup-task-mention-btn">
                <AtSign className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {colleagues.map((c) => (
                <DropdownMenuItem key={c.user_id} onClick={() => addMention(c)} data-testid={`mention-${c.user_id}`}>
                  {c.picture
                    ? <img src={c.picture} alt="" className="h-5 w-5 rounded-full object-cover mr-2" />
                    : <AtSign className="h-4 w-4 mr-2 text-zinc-400" />}
                  {c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Button size="icon" className="bg-ink hover:bg-black shrink-0" onClick={send} disabled={busy} data-testid="sup-task-msg-send"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

/* Per-job hand-over: give this one task to a teammate (no yacht-wide access). */
function HandoverControl({ job, onChanged }) {
  const { user } = useAuth();
  const [colleagues, setColleagues] = useState([]);
  useEffect(() => {
    if (user?.is_supplier) {
      api.get("/supplier/team/colleagues")
        .then(({ data }) => setColleagues(data || []))
        .catch(() => {});
    }
  }, [user?.is_supplier]);
  if (!user?.is_supplier || (!colleagues.length && !job.handled_by && !(job.handover_log || []).length)) return null;
  const hand = async (uid) => {
    try {
      const { data } = await api.post(`/maintenance/${job.id}/handover`, { user_id: uid });
      toast.success(uid ? `Task handed over to ${data.handled_by_name}` : "Hand-over cleared");
      onChanged?.(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const options = colleagues.filter((c) => c.user_id !== job.handled_by);
  const log = [...(job.handover_log || [])].slice(-4).reverse();
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid="sup-task-handover">
      <div className="text-xs text-zinc-600 min-w-0 truncate" data-testid="sup-task-handled-by">
        {job.handled_by_name
          ? <>Handled by <span className="font-medium text-zinc-800">{job.handled_by_name}</span></>
          : "Hand this task over to a teammate"}
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className="h-7 shrink-0 text-xs" data-testid="sup-task-handover-btn">
            <ArrowRightLeft className="h-3.5 w-3.5 mr-1.5" />Hand over
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {options.length === 0 && !job.handled_by && (
            <DropdownMenuItem disabled>No teammates available</DropdownMenuItem>
          )}
          {options.map((c) => (
            <DropdownMenuItem key={c.user_id} onClick={() => hand(c.user_id)} data-testid={`handover-option-${c.user_id}`}>
              {c.picture
                ? <img src={c.picture} alt="" className="h-5 w-5 rounded-full object-cover mr-2" />
                : <ArrowRightLeft className="h-4 w-4 mr-2 text-zinc-400" />}
              {c.name}
            </DropdownMenuItem>
          ))}
          {job.handled_by && (
            <DropdownMenuItem onClick={() => hand(null)} className="text-red-600" data-testid="handover-clear">
              Clear hand-over
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      </div>
      {log.length > 0 && (
        <div className="rounded-md border border-zinc-100 bg-white px-3 py-1.5 space-y-0.5" data-testid="sup-task-handover-log">
          {log.map((e, i) => (
            <div key={i} className="text-[11px] text-zinc-500" data-testid={`handover-log-row-${i}`}>
              {e.to_name
                ? <>Handed to <span className="font-medium text-zinc-700">{e.to_name}</span>{e.from_name ? <> (from {e.from_name})</> : null}</>
                : <>Hand-over cleared{e.from_name ? <> (was {e.from_name})</> : null}</>}
              {" · by "}{e.by_name}{" · "}{(e.at || "").slice(0, 10)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* Supplier-side task detail — invoice-style layout (spec order 2026-06):
   yacht · task name + review · category/status · priority/deadline ·
   description · messages · scope · before media · after media · documents. */
export default function SupplierTaskDetail({ job, onClose, onRefresh }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(job);
  const [review, setReview] = useState(null);
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/maintenance/${job.id}`).then(({ data }) => {
      if (alive && data) setCurrent((c) => ({ ...c, ...data, yacht_name: c.yacht_name || data.yacht_name }));
    }).catch(() => {});
    api.get(`/maintenance/${job.id}/review`).then(({ data }) => {
      if (alive) setReview(data.review);
    }).catch(() => {});
    return () => { alive = false; };
  }, [job.id]);

  const qid = current.assigned_supplier?.quote_id;
  const loadQuote = useCallback(async () => {
    if (!qid) return;
    try { const { data } = await api.get(`/quotes/${qid}`); setQuote(data); } catch { /* ignore */ }
  }, [qid]);
  useEffect(() => { loadQuote(); }, [loadQuote]);

  // 3. Status — forward-only supplier dropdown; a confirmed task is final and
  //    shows a plain badge instead.
  const statusLocked = ["confirmed", "cancelled"].includes(current.status) || !!current.deleted_at;
  const statusChoices = (() => {
    const cur = current.status;
    const curIdx = VENDOR_STATUS.indexOf(cur);
    const lockBack = cur !== "cancelled" && curIdx >= VENDOR_STATUS.indexOf("approved");
    const noCancel = NO_CANCEL_STATUSES.has(cur);
    const base = VENDOR_STATUS.filter((s) => {
      if (s === cur) return true;
      if (PROVIDER_FORBIDDEN_STATUSES.has(s)) return false;
      // Online jobs: "Cancelled" stays selectable even after prepay — picking
      // it then opens the owner-approval request instead of cancelling directly.
      if (s === "cancelled") return !noCancel || !!qid;
      // Dispute rework: the supplier re-completes the job after fixing it.
      if (cur === "dispute" && s === "completed") return true;
      // Prepay negotiation: re-request an adjusted prepayment after "too high".
      if (cur === "prepay_too_high" && s === "waiting_prepay") return true;
      if (lockBack && VENDOR_STATUS.indexOf(s) < curIdx) return false;
      return true;
    });
    return base.includes(cur) ? base : [cur, ...base];
  })();
  const [prepayOpen, setPrepayOpen] = useState(false);
  // Online-job cancellation (user rules 2026-06): direct cancel needs a note;
  // from prepay_done on it becomes an approval request to the owner.
  const onlineJob = !!qid;
  const [cxlOpen, setCxlOpen] = useState(false);
  const [cxlNote, setCxlNote] = useState("");
  const [cxlMode, setCxlMode] = useState("direct");
  const [feeAmt, setFeeAmt] = useState("");
  const canRequestCancel = onlineJob && !statusLocked && !current.cancel_request
    && NO_CANCEL_STATUSES.has(current.status) && current.status !== "full_payment_confirmed";
  const submitCxl = async () => {
    const note = cxlNote.trim();
    if (cxlMode !== "reject" && cxlMode !== "approve" && !note) return;
    setCxlOpen(false);
    if (cxlMode === "direct") { await saveStatus("cancelled", { note }); await loadQuote(); return; }
    try {
      if (cxlMode === "approve") {
        const fee = feeAmt.trim() === "" ? null : Number(feeAmt);
        if (fee !== null && (Number.isNaN(fee) || fee <= 0)) { toast.error("Enter a cancellation fee greater than 0, or leave it empty."); return; }
        const { data } = await api.post(`/maintenance/${current.id}/cancel-request/approve`, { fee_amount: fee });
        if (data) setCurrent((c) => ({ ...c, ...data, yacht_name: c.yacht_name }));
        toast.success(fee ? `Fee of ${jobCurrency || "EUR"} ${fee.toFixed(2)} proposed — waiting for the owner's approval` : "Cancellation approved — job cancelled");
        await loadQuote();
        await onRefresh?.();
        return;
      }
      const url = cxlMode === "reject"
        ? `/maintenance/${current.id}/cancel-request/reject`
        : `/maintenance/${current.id}/cancel-request`;
      const { data } = await api.post(url, { note });
      if (data) setCurrent((c) => ({ ...c, ...data, yacht_name: c.yacht_name }));
      toast.success(cxlMode === "reject" ? "Cancellation request rejected" : "Cancellation request sent to the yacht");
      await loadQuote();
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const resolveCancelReq = (action) => async () => {
    if (action === "reject") { setCxlNote(""); setCxlMode("reject"); setCxlOpen(true); return; }
    // Approving a late yacht cancellation: offer the optional compensation fee.
    setFeeAmt(""); setCxlNote(""); setCxlMode("approve"); setCxlOpen(true);
  };
  const { user: authUser } = useAuth();
  const canInvoice = authUser?.supplier_permissions?.can_invoice;
  const [prepayPct, setPrepayPct] = useState("");
  const [prepayAmt, setPrepayAmt] = useState("");
  const jobCost = Number(current.cost || 0);
  const jobCurrency = current.assigned_supplier?.currency || "";
  const fmtPrepay = (v) => `${jobCurrency ? jobCurrency + " " : ""}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const openPrepay = () => {
    if (canInvoice === false) {
      toast.error("Your company admin hasn't allowed you to request prepayments.");
      return;
    }
    const pct = current.prepay_percent != null ? String(current.prepay_percent) : "30";
    setPrepayPct(pct);
    setPrepayAmt(jobCost
      ? (jobCost * Number(pct) / 100).toFixed(2)
      : (current.prepay_amount != null ? String(current.prepay_amount) : ""));
    setPrepayOpen(true);
  };
  const onPctChange = (v) => {
    setPrepayPct(v);
    const n = Number(v);
    if (jobCost && v !== "" && !Number.isNaN(n)) setPrepayAmt((jobCost * n / 100).toFixed(2));
  };
  const submitPrepay = async () => {
    const amt = Number(prepayAmt);
    if (!amt || amt <= 0) { toast.error("Enter a prepayment amount greater than 0."); return; }
    if (jobCost && amt > jobCost) { toast.error("The prepayment can't exceed the agreed job cost."); return; }
    setPrepayOpen(false);
    await saveStatus("waiting_prepay", {
      prepay_percent: prepayPct !== "" && !Number.isNaN(Number(prepayPct)) ? Number(prepayPct) : null,
      prepay_amount: amt,
    });
  };
  const saveStatus = async (status, extra = {}) => {
    try {
      const { data } = await api.patch(`/maintenance/${current.id}/status`, { status, ...extra });
      if (data) setCurrent((c) => ({ ...c, ...data, yacht_name: c.yacht_name }));
      toast.success(`Status → ${t(`tasks.status_${status}`)}`);
      await onRefresh?.();
    } catch (e) {
      const msg = fmtErr(e);
      if (/iban|banking/i.test(msg)) {
        if (authUser?.supplier_role === "manager") {
          // Managers can't edit company bank details — the backend message
          // already points them to their admin (who also gets notified).
          toast.error(msg, { duration: 9000 });
        } else {
          toast.error(msg, {
            duration: 9000,
            action: { label: "Open profile", onClick: () => { window.location.href = "/app/profile"; } },
          });
        }
      } else toast.error(msg);
    }
  };

  const patchPhotos = (data) => { if (data) setCurrent((c) => ({ ...c, ...data, yacht_name: c.yacht_name })); };
  const addAfter = async (path) => {
    try { const { data } = await api.post(`/maintenance/${current.id}/photos/after`, { photos: [path] }); patchPhotos(data); await onRefresh?.(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const deleteAfter = async (path) => {
    const { data } = await api.delete(`/maintenance/${current.id}/photos/after`, { data: { photos: [path] } });
    patchPhotos(data);
    await onRefresh?.();
  };
  const addDocument = async (path, name, kind) => {
    try {
      const { data } = await api.post(`/maintenance/${current.id}/documents`, { file_path: path, name, kind });
      patchPhotos(data);
      toast.success("Document attached");
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const removeDocument = async (docId) => {
    try { const { data } = await api.delete(`/maintenance/${current.id}/documents/${docId}`); patchPhotos(data); await onRefresh?.(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const categories = (current.system_categories && current.system_categories.length)
    ? current.system_categories : (current.system_category ? [current.system_category] : []);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 p-4 sm:p-6" data-testid="sup-task-detail">
        {/* 1. Yacht name · 2. task name + review */}
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg pr-6" data-testid="sup-task-yacht-name">{current.yacht_name || "Yacht"}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex items-center gap-2 flex-wrap" data-testid="sup-task-title-row">
              <span className="text-sm font-medium text-zinc-900">{current.title}</span>
              {review && (
                <span title={review.comment || undefined} data-testid="sup-task-review-stars">
                  <StarsDisplay value={review.rating} />
                </span>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        <HandoverControl
          job={current}
          onChanged={(data) => {
            setCurrent((c) => ({ ...c, ...data, yacht_name: c.yacht_name, handled_by: data.handled_by || null, handled_by_name: data.handled_by_name || null }));
            onRefresh?.();
          }}
        />

        {["confirmed", "full_payment_confirmed"].includes(current.status) && (
          <div className="-mt-1">
            <ShowInvoiceButton invoiceId={current.invoice_id} supplier testid="sup-task-show-invoice" />
          </div>
        )}

        {current.status === "waiting_prepay" && current.prepay_amount != null && (
          <div className="rounded-md border border-orange-200 bg-orange-50 text-orange-800 px-3 py-2 text-xs" data-testid="sup-task-prepay-banner">
            <span className="font-semibold">Prepayment requested:</span> {fmtPrepay(current.prepay_amount)}
            {current.prepay_percent ? ` (${current.prepay_percent}%)` : ""}. Waiting for the yacht to pay or respond.
          </div>
        )}
        {current.status === "prepay_too_high" && (
          <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-xs" data-testid="sup-task-prepay-too-high-banner">
            <span className="font-semibold">Prepayment too high:</span> the yacht asked you to adjust the requested prepayment
            {current.prepay_amount != null ? ` of ${fmtPrepay(current.prepay_amount)}` : ""}.
            {current.prepay_note && <> Note from the yacht: “{current.prepay_note}”.</>}
            <div className="mt-1.5">
              <Button size="sm" variant="outline" className="h-7 border-rose-300 text-rose-700 hover:bg-rose-100" onClick={openPrepay} data-testid="sup-task-adjust-prepay-btn">
                Adjust prepayment
              </Button>
            </div>
          </div>
        )}
        {current.prepay_paid && current.prepay_amount != null && (
          <div className="rounded-md border border-teal-200 bg-teal-50 text-teal-800 px-3 py-2 text-xs" data-testid="sup-task-prepay-paid">
            <span className="font-semibold">Prepayment received:</span> {fmtPrepay(current.prepay_amount)} — it will be deducted on the final invoice.
            {(current.documents || []).some((d) => d.kind === "payment" && (d.name || "").startsWith("Prepayment receipt")) && (
              <> The transfer receipt is attached below in Documents.</>
            )}
          </div>
        )}

        {current.status === "dispute" && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-xs" data-testid="sup-task-dispute-banner">
            <span className="font-semibold">Work disputed{current.dispute_count > 1 ? ` (round ${current.dispute_count})` : ""}:</span>{" "}
            {current.dispute_reason || "The yacht did not accept the completed work."} Please fix the issue, then set the status back to <span className="font-semibold">Completed</span>.
            {(current.rework_deadline || "").slice(0, 10) && (
              <div className="mt-1" data-testid="sup-task-rework-deadline">
                <span className="font-semibold">Rework deadline:</span> {(current.rework_deadline || "").slice(0, 10)}
                {(current.rework_deadline || "").slice(0, 10) < new Date().toISOString().slice(0, 10) && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-red-600 text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="sup-task-rework-overdue-badge">
                    Overdue
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {current.cancel_request && (
          <div className="rounded-md border border-rose-200 bg-rose-50 text-rose-900 px-3 py-2 text-xs" data-testid="sup-task-cancel-request-banner">
            {current.cancel_request.requested_by_kind === "requester" ? (
              current.cancel_request.state === "fee_proposed" ? (
                <>
                  <span className="font-semibold">Fee proposed:</span>{" "}
                  you offered to cancel for {current.cancel_request.fee_currency}{" "}
                  {Number(current.cancel_request.fee_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — waiting for the owner to accept or decline.
                  <span className="ml-2 inline-flex gap-3">
                    <button type="button" className="underline font-semibold" data-testid="sup-task-change-fee" onClick={resolveCancelReq("approve")}>Change fee</button>
                    <button type="button" className="underline" data-testid="sup-task-cancel-req-reject" onClick={resolveCancelReq("reject")}>Reject request</button>
                  </span>
                </>
              ) : (
                <>
                  <span className="font-semibold">The yacht requests to cancel this job:</span> “{current.cancel_request.note}”
                  {current.cancel_request.fee_declined_at && (
                    <span className="block mt-0.5 text-rose-700/80">
                      Your fee of {current.cancel_request.fee_declined_currency} {Number(current.cancel_request.fee_declined_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} was declined
                      {current.cancel_request.fee_declined_note ? `: “${current.cancel_request.fee_declined_note}”` : "."} Propose a new fee, approve without one, or reject.
                    </span>
                  )}
                  <span className="ml-2 inline-flex gap-3">
                    <button type="button" className="underline font-semibold" data-testid="sup-task-cancel-req-approve" onClick={resolveCancelReq("approve")}>Approve</button>
                    <button type="button" className="underline" data-testid="sup-task-cancel-req-reject" onClick={resolveCancelReq("reject")}>Reject</button>
                  </span>
                </>
              )
            ) : (
              <>
                <span className="font-semibold">Cancellation requested</span> — waiting for the yacht's approval. Note: “{current.cancel_request.note}”
              </>
            )}
          </div>
        )}

        <Dialog open={cxlOpen} onOpenChange={setCxlOpen}>
          <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-sm" data-testid="sup-task-cancel-note-dialog">
            <DialogHeader><DialogTitle>{cxlMode === "direct" ? "Cancel this job" : cxlMode === "reject" ? "Reject cancellation request" : cxlMode === "approve" ? "Approve cancellation" : "Request cancellation"}</DialogTitle></DialogHeader>
            <DialogDescription className="sr-only">Cancellation note</DialogDescription>
            <p className="text-sm text-zinc-600">
              {cxlMode === "direct"
                ? "The job will be cancelled and the yacht informed. Add a short note to the owner explaining why."
                : cxlMode === "reject"
                ? "The job stays active. You can add an optional note to the yacht explaining why."
                : cxlMode === "approve"
                ? "Approving without a fee cancels the job immediately. If you attach a cancellation fee, the owner must accept it first — once accepted the job is cancelled and a fee invoice is created for you to send."
                : "The prepayment was made — cancelling now needs the owner's approval. Add a note explaining why."}
            </p>
            {cxlMode === "approve" ? (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Cancellation fee (optional{jobCurrency ? ` — ${jobCurrency}` : ""})</div>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={feeAmt}
                  onChange={(e) => setFeeAmt(e.target.value)}
                  placeholder="e.g. 250.00 — leave empty for no fee"
                  data-testid="sup-task-cancel-fee-input"
                />
                {(() => {
                  // Fair-fee guideline by job progress (user request 2026-06).
                  const FEE_GUIDE = {
                    prepay_done: [10, "the job was committed but work hasn't started"],
                    waiting_parts: [20, "parts are already ordered"],
                    in_progress: [50, "work is underway"],
                    completed: [80, "the work is essentially done"],
                    dispute: [80, "the work is essentially done"],
                  };
                  const g = FEE_GUIDE[current.status];
                  if (!g || !jobCost) return null;
                  const sug = Math.round(jobCost * g[0]) / 100;
                  return (
                    <p className="text-[11px] text-zinc-500 mt-1" data-testid="sup-task-fee-guideline">
                      Guideline: since {g[1]}, a fee of about <span className="font-semibold">{g[0]}%</span> of the agreed cost is common.{" "}
                      <button type="button" className="underline text-rose-600 font-medium" data-testid="sup-task-fee-suggest-btn" onClick={() => setFeeAmt(sug.toFixed(2))}>
                        Use {fmtPrepay(sug)}
                      </button>
                    </p>
                  );
                })()}
                {current.prepay_paid && current.prepay_amount != null && (
                  <p className="text-[11px] text-zinc-500 mt-1">The prepayment you already received ({fmtPrepay(current.prepay_amount)}) will show as a deduction on the fee invoice.</p>
                )}
              </div>
            ) : (
              <Textarea value={cxlNote} onChange={(e) => setCxlNote(e.target.value)} placeholder="Note to the owner…" rows={3} data-testid="sup-task-cancel-note-input" />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setCxlOpen(false)} data-testid="sup-task-cancel-note-back">Back</Button>
              <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={cxlMode !== "reject" && cxlMode !== "approve" && !cxlNote.trim()} onClick={submitCxl} data-testid="sup-task-cancel-note-submit">
                {cxlMode === "direct" ? "Cancel job" : cxlMode === "reject" ? "Reject request" : cxlMode === "approve" ? (feeAmt.trim() ? "Approve with fee" : "Approve cancellation") : "Send request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 3. Category · Status  /  4. Priority · Deadline */}
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div data-testid="sup-task-category">
            <MiniLabel>{categories.length > 1 ? "Categories" : "Category"}</MiniLabel>
            <div className="flex items-center gap-1 flex-wrap mt-0.5">
              {categories.length
                ? categories.map((c) => <span key={c} className="inline-flex rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs">{c}</span>)
                : "—"}
            </div>
          </div>
          <div data-testid="sup-task-status">
            <MiniLabel>Status</MiniLabel>
            <div className="mt-0.5">
              {statusLocked ? (
                <StatusBadge value={current.status} />
              ) : (
                <select
                  value={current.status}
                  onChange={(e) => {
                    if (e.target.value === "waiting_prepay") { openPrepay(); e.target.value = current.status; }
                    else if (e.target.value === "cancelled" && onlineJob) {
                      // After prepay the owner must approve — route into the request flow.
                      const needsApproval = NO_CANCEL_STATUSES.has(current.status);
                      if (needsApproval && current.cancel_request) { toast.info("A cancellation request is already pending."); }
                      else { setCxlNote(""); setCxlMode(needsApproval ? "request" : "direct"); setCxlOpen(true); }
                      e.target.value = current.status;
                    }
                    else saveStatus(e.target.value);
                  }}
                  className="h-8 rounded-md border border-input px-2 text-sm bg-white max-w-full"
                  data-testid="sup-task-status-select"
                >
                  {statusChoices.map((s) => <option key={s} value={s}>{t(`tasks.status_${s}`)}</option>)}
                </select>
              )}
              <div className="text-[10px] text-zinc-400 mt-0.5" data-testid="sup-task-status-date">
                since {(current.status_changed_at || current.updated_at || current.created_at || "").slice(0, 10)}
              </div>
              {canRequestCancel && (
                <button
                  type="button"
                  className="text-[11px] text-rose-600 underline underline-offset-2 mt-1"
                  data-testid="sup-task-request-cancel-btn"
                  onClick={() => { setCxlNote(""); setCxlMode("request"); setCxlOpen(true); }}
                >
                  Request cancellation (needs the yacht's approval)
                </button>
              )}
            </div>
          </div>
          <div data-testid="sup-task-priority">
            <MiniLabel>Priority</MiniLabel>
            <div className="mt-0.5"><PriorityPill v={current.priority} /></div>
          </div>
          <div data-testid="sup-task-deadline">
            <MiniLabel>Deadline</MiniLabel>
            <div className="mt-0.5">{(current.due_date || "").slice(0, 10) || "—"}</div>
          </div>
          {current.cancellation_fee?.amount != null && (
            <div className="col-span-2" data-testid="sup-task-cancellation-fee">
              <MiniLabel>Cancellation fee</MiniLabel>
              <div className="mt-0.5 text-rose-700 font-medium text-sm">
                {current.cancellation_fee.currency} {Number(current.cancellation_fee.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — send the fee invoice from your Invoices page
              </div>
            </div>
          )}
        </div>

        {/* 5. Description */}
        {current.description && (
          <div data-testid="sup-task-description">
            <MiniLabel>Description</MiniLabel>
            <div className="text-sm text-zinc-700 whitespace-pre-wrap mt-0.5">{current.description}</div>
          </div>
        )}

        {/* 6. Scope (no supplier name) */}
        {quote && ((quote.scope_items || []).length > 0 || quote.notes) && (
          <div className="mt-1 border-t border-zinc-200 pt-3" data-testid="sup-task-scope">
            <MiniLabel>Scope</MiniLabel>
            {(quote.scope_items || []).length > 0 && (
              <div className="rounded-md border border-emerald-100 bg-emerald-50/40 p-2 mt-1">
                <ul className="space-y-0.5">
                  {quote.scope_items.map((s, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 text-sm text-zinc-700">
                      <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]"><span className="text-zinc-400 font-medium mr-1">{i + 1}.</span>{s.text}</span>
                      {s.price != null && <span className="text-zinc-500 text-xs whitespace-nowrap shrink-0">{quote.currency} {s.price}</span>}
                    </li>
                  ))}
                </ul>
                <VatBreakdown items={quote.scope_items} amount={quote.amount} currency={quote.currency} vatPercent={quote.vat_percent} testid="sup-task-vat" />
                <div className="mt-1 pt-1 border-t border-emerald-100 flex items-baseline justify-between text-sm">
                  <span className="text-zinc-500 text-xs">Agreed total{quote.vat_percent != null ? " incl. VAT" : ""}</span>
                  <span className="font-medium text-zinc-800">{quote.currency} {quote.amount}</span>
                </div>
              </div>
            )}
            {quote.notes && <p className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{quote.notes}</p>}
          </div>
        )}

        {/* 7. Messages — under Scope (user spec 2026-06) */}
        {quote && (
          <div className="mt-1 border-t border-zinc-200 pt-3">
            <MiniLabel>Messages</MiniLabel>
            <QuoteThread quote={quote} onChanged={loadQuote} />
          </div>
        )}

        {/* 8. Before media (yacht's — read-only) · 9. After media (yours) */}
        <PhotoBucket
          title="Before Media"
          paths={current.before_photos || []}
          canAdd={false}
          canDelete={false}
          yacht_id={current.yacht_id}
          folder="maintenance/before"
          testid="before"
        />
        <PhotoBucket
          title="After Media"
          paths={current.after_photos || []}
          canAdd
          canDelete
          yacht_id={current.yacht_id}
          folder="maintenance/after"
          onUploaded={addAfter}
          onDelete={deleteAfter}
          testid="after"
          helper="You're the assigned service provider — upload your completion photos here."
        />

        <DocumentsBucket
          documents={current.documents || []}
          canAdd
          canRemoveAny={false}
          userId={current.assigned_to}
          yacht_id={current.yacht_id}
          onUploaded={addDocument}
          onRemove={removeDocument}
        />

        {/* History — below Documents (user spec 2026-06) */}
        <TaskHistory taskId={current.id} refreshKey={current.status} />

        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={onClose} data-testid="sup-task-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>

      {prepayOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setPrepayOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="prepay-request-dialog">
            <DialogHeader>
              <DialogTitle>{current.status === "prepay_too_high" ? "Adjust prepayment" : "Request prepayment"}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-600">
              {jobCost > 0 ? <>Agreed job cost: <span className="font-semibold">{fmtPrepay(jobCost)}</span>. </> : null}
              Set a percentage — the amount is calculated for you — then fine-tune the final amount if needed.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-zinc-600">Percentage (%)</label>
                <Input
                  type="number" min="1" max="100" step="1"
                  value={prepayPct}
                  onChange={(e) => onPctChange(e.target.value)}
                  className="mt-1"
                  data-testid="prepay-percent-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-600">Final amount{jobCurrency ? ` (${jobCurrency})` : ""}</label>
                <Input
                  type="number" min="0.01" step="0.01"
                  value={prepayAmt}
                  onChange={(e) => setPrepayAmt(e.target.value)}
                  className="mt-1"
                  data-testid="prepay-amount-input"
                />
              </div>
            </div>
            <p className="text-[11px] text-zinc-500">
              The yacht will be notified and can pay it (Prepay done) or reply "Prepay too high" — then you can adjust and re-send.
            </p>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setPrepayOpen(false)} data-testid="prepay-request-cancel">Cancel</Button>
              <Button onClick={submitPrepay} disabled={!prepayAmt || Number(prepayAmt) <= 0} data-testid="prepay-request-submit">
                {current.status === "prepay_too_high" ? "Re-request prepayment" : "Request prepayment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
